import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { identifyUser } from '../lib/logrocket.js';

// Reviewer inbox (v4 design).
//
// The dashboard's job is to surface what needs a decision. Status tabs split
// the funnel — Inbox is the only one that strictly requires review work,
// the rest are reference. Within a tab the reviewer picks Unread vs All:
// "Unread" is read-state we track client-side, so closing your laptop and
// coming back tomorrow gives you the smaller queue, not the same 50 items
// you already eyeballed.
//
// Slack stays the primary surface for fast yes/no triage on individual
// submissions; this dashboard is for the cases where you want to actually
// watch the clip, read the transcript, or fire the +$20 usage bonus.
//
// All wiring is unchanged from v3:
//   • GET  /api/admin/whoami    →  identify LogRocket session
//   • GET  /api/dashboard/submissions?status=X
//   • POST /api/submission/:id/approve   (bulk-approve & pay)
//   • POST /api/invite                   (invite-customer modal)
//   • POST /api/admin/logout

const STATUS_TABS = [
  { key: 'submitted',   label: 'Inbox',    hint: 'Waiting on you'           },
  { key: 'approved',    label: 'Approved', hint: 'Paid out, awaiting use'   },
  { key: 'paid',        label: 'Paid',     hint: 'Gift card delivered'      },
  { key: 'rejected',    label: 'Rejected', hint: 'Excluded from payout'     },
  { key: 'in_progress', label: 'Not yet',  hint: "Hasn't submitted"         },
];

// Deterministic avatar gradient by customer name. Lets reviewers visually
// anchor to "the green one" or "the purple one" when scanning the inbox.
// 6 distinct on-brand-adjacent gradients. Picked from the v4 handoff.
const AVATAR_PALETTES = [
  ['#FFC79F', '#F53900'], // orange (brand)
  ['#7FD7AA', '#1A7A4D'], // green
  ['#F2A6BB', '#D43A6F'], // pink
  ['#FFCD7A', '#F5B400'], // amber
  ['#A4B7FF', '#6461FC'], // indigo
  ['#FFAA94', '#FB522C'], // coral
];
function avatarPalette(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTES[h % AVATAR_PALETTES.length];
}
function avatarStyle(name) {
  const [a, b] = avatarPalette(name);
  return { background: `linear-gradient(135deg, ${a}, ${b})` };
}
function avatarInitials(name) {
  return (name || '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

// AI score → color tier. Thresholds match the Slack post and the
// verifyClip rubric in the worker: 8-10 = good, 5-7 = fair, 1-4 = poor.
// Kept here as a tiny shared helper so the same thresholds apply
// everywhere a score is rendered in the dashboard.
function verifyTier(score) {
  if (score == null) return 'pending';
  if (score >= 8) return 'good';
  if (score >= 5) return 'fair';
  return 'poor';
}

// Read-state persistence key. Keeps "what have I seen today" across page
// refreshes without needing a server round-trip on every row open.
const READ_IDS_KEY = 'vt-admin-read-ids';
function loadReadIds() {
  try {
    const raw = localStorage.getItem(READ_IDS_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}
function saveReadIds(set) {
  try { localStorage.setItem(READ_IDS_KEY, JSON.stringify([...set])); } catch {}
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('status') || 'submitted';

  const [rows, setRows] = useState([]);
  // We also fetch the "submitted" tab once on the side so the Unread badge
  // and the Inbox-tab count stay accurate when the reviewer is on a different
  // tab. Avoids the "you have 5 unread but the tab shows 0" inconsistency.
  const [inboxRows, setInboxRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInvite, setShowInvite] = useState(false);

  // View / filter / pagination state — local only, never round-trips
  const [view, setView] = useState('unread');     // 'unread' | 'all'
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');     // 'newest' | 'oldest' | 'most-clips'
  const [selected, setSelected] = useState(() => new Set());
  const [shown, setShown] = useState(25);
  const [readIds, setReadIds] = useState(loadReadIds);

  const [bulkBusy, setBulkBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  // Tag the LogRocket session with the admin's identity on mount. whoami
  // returns { admin, email } and the email cookie is set at magic-link
  // callback time. Best-effort: sessions created before the email cookie
  // existed just skip the identify step.
  useEffect(() => {
    let alive = true;
    api('/api/admin/whoami')
      .then((r) => {
        if (alive && r.admin && r.email) identifyUser({ email: r.email, role: 'admin' });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const load = useCallback(async (s) => {
    setLoading(true);
    setError('');
    try {
      const r = await api(`/api/dashboard/submissions?status=${encodeURIComponent(s)}`);
      setRows(r.submissions || []);
    } catch (e) {
      if (e.status === 401) { navigate('/dashboard/login'); return; }
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Manual "Sync now": reconcile every pending payout against Tremendous and
  // promote delivered submissions to 'paid'. Interim stand-in for the
  // delivery webhook; admin-triggered so there's no automatic API polling.
  const syncPayouts = useCallback(async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const r = await api('/api/dashboard/sync-payouts', { method: 'POST' });
      const bits = [];
      if (r.delivered) bits.push(`${r.delivered} marked delivered`);
      if (r.promoted) bits.push(`${r.promoted} now paid`);
      setSyncMsg(bits.length ? `Synced: ${bits.join(', ')}.` : 'All up to date.');
      load(tab);
    } catch (e) {
      setSyncMsg(e?.status === 401 ? 'Session expired — please log in again.' : 'Sync failed. Try again.');
    } finally {
      setSyncing(false);
    }
  }, [tab, load]);

  useEffect(() => { load(tab); }, [tab, load]);

  // Side-load the "submitted" tab for the inbox-unread badge.
  // Only refetch when the user is NOT already on it (otherwise just reuse rows).
  useEffect(() => {
    if (tab === 'submitted') { setInboxRows(rows); return; }
    let alive = true;
    api('/api/dashboard/submissions?status=submitted')
      .then((r) => { if (alive) setInboxRows(r.submissions || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [tab, rows]);

  // Reset view-level state when switching tabs/views
  useEffect(() => {
    setSelected(new Set());
    setShown(25);
  }, [tab, view]);

  // ── Read-state ────────────────────────────────────────────────────────
  // A submission is "unread" if it's never been opened (id not in readIds).
  // Only matters on the Inbox tab — once approved/paid/rejected, there's no
  // reviewer work left to do so the unread distinction is noise.
  const isUnread = useCallback((s) => !readIds.has(s.id), [readIds]);

  const inboxUnreadCount = useMemo(
    () => inboxRows.filter(isUnread).length,
    [inboxRows, isUnread]
  );

  // Tab counts: respect the active view so "Unread" tab counts only show
  // unread items in that tab — matches how the rows are filtered below.
  const countForTab = (key) => {
    // For tabs other than submitted/Inbox the unread distinction doesn't
    // apply (no review work to do), so we always show total.
    if (key !== 'submitted') {
      if (key === tab) return rows.length;
      return undefined; // we don't side-load every tab; show no count
    }
    if (view === 'unread') return inboxRows.filter(isUnread).length;
    return inboxRows.length;
  };

  // ── Filter + sort the visible list ────────────────────────────────────
  let visibleRows = rows;
  if (view === 'unread' && tab === 'submitted') {
    visibleRows = visibleRows.filter(isUnread);
  }
  if (query.trim()) {
    const q = query.trim().toLowerCase();
    visibleRows = visibleRows.filter((s) =>
      (s.customer_name  || '').toLowerCase().includes(q) ||
      (s.customer_email || '').toLowerCase().includes(q) ||
      (s.invite_token   || '').toLowerCase().includes(q)
    );
  }
  visibleRows = [...visibleRows].sort((a, b) => {
    if (sort === 'oldest')    return (a.submitted_at || a.created_at || 0) - (b.submitted_at || b.created_at || 0);
    if (sort === 'most-clips') return (b.clip_count || 0) - (a.clip_count || 0);
    return (b.submitted_at || b.created_at || 0) - (a.submitted_at || a.created_at || 0);
  });

  const total = visibleRows.length;
  const visible = visibleRows.slice(0, shown);
  const hasMore = total > shown;

  // ── Actions ───────────────────────────────────────────────────────────
  const openWithRead = (id) => {
    if (!readIds.has(id)) {
      const next = new Set(readIds);
      next.add(id);
      setReadIds(next);
      saveReadIds(next);
    }
    navigate(`/dashboard/submissions/${id}`);
  };

  const markAllRead = () => {
    const next = new Set(readIds);
    inboxRows.forEach((s) => next.add(s.id));
    setReadIds(next);
    saveReadIds(next);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(visible.map((s) => s.id)));
  const clearSelection = () => setSelected(new Set());

  async function bulkApprove() {
    if (bulkBusy) return;
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!confirm(`Approve and pay out ${ids.length} submission${ids.length === 1 ? '' : 's'}? Each fires a Tremendous order. This is irreversible.`)) return;
    setBulkBusy(true);
    const results = [];
    for (const id of ids) {
      try {
        const r = await api(`/api/submission/${id}/approve`, {
          method: 'POST',
          body: { reviewer_email: localStorage.getItem('vt-reviewer-email') || 'unknown' },
        });
        results.push({ id, ok: true, amountCents: r.amountCents });
      } catch (e) {
        results.push({ id, ok: false, error: e.message || 'failed' });
      }
    }
    setBulkBusy(false);
    const okCount    = results.filter((r) => r.ok).length;
    const totalCents = results.reduce((s, r) => s + (r.amountCents || 0), 0);
    const failed     = results.filter((r) => !r.ok);
    alert(
      `Approved ${okCount} of ${ids.length}. ` +
      (totalCents ? `$${(totalCents / 100).toFixed(2)} in payouts queued. ` : '') +
      (failed.length ? `\nErrors:\n${failed.map((f) => `• ${f.id}: ${f.error}`).join('\n')}` : '')
    );
    clearSelection();
    await load(tab);
  }

  async function logout() {
    try { await api('/api/admin/logout', { method: 'POST' }); } catch {}
    navigate('/dashboard/login', { replace: true });
  }

  const isSubmittedTab = tab === 'submitted';
  const allVisibleSelected = visible.length > 0 && visible.every((s) => selected.has(s.id));

  return (
    <main className="admin-page">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="admin-header">
        <div className="admin-header-text">
          <h1 className="admin-h1">Reviews</h1>
          <p className="admin-h1-sub">
            {inboxUnreadCount > 0
              ? <><strong>{inboxUnreadCount}</strong> waiting on you</>
              : <>All caught up.</>}
            {' · '}
            <span className="admin-h1-sub-quiet">{inboxRows.length} in inbox</span>
          </p>
        </div>
        <div className="admin-header-actions">
          <button className="admin-invite-btn" onClick={() => setShowInvite(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            <span>Invite customer</span>
          </button>
          <button className="admin-invite-btn" onClick={logout} title="Sign out">
            Sign out
          </button>
        </div>
      </div>

      {/* ── View toggle (only meaningful on Inbox tab) ──────────────────── */}
      {isSubmittedTab && (
        <div className="admin-view-row">
          <div className="admin-view-toggle" role="tablist" aria-label="Inbox view">
            <button
              className={'view-btn' + (view === 'unread' ? ' active' : '')}
              onClick={() => setView('unread')}
              role="tab"
              aria-selected={view === 'unread'}
            >
              <span>Unread</span>
              <span className="view-btn-count">{inboxUnreadCount}</span>
            </button>
            <button
              className={'view-btn' + (view === 'all' ? ' active' : '')}
              onClick={() => setView('all')}
              role="tab"
              aria-selected={view === 'all'}
            >
              <span>All</span>
              <span className="view-btn-count">{inboxRows.length}</span>
            </button>
          </div>
          {view === 'unread' && inboxUnreadCount > 0 && (
            <button className="admin-mark-read" onClick={markAllRead}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Mark all as read
            </button>
          )}
        </div>
      )}

      {/* ── Search + sort ───────────────────────────────────────────────── */}
      <div className="admin-toolbar">
        <div className="admin-search">
          <svg className="admin-search-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="search"
            className="admin-search-input"
            placeholder="Search by name, email, or invite token…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="admin-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M6 6l12 12M6 18L18 6"/></svg>
            </button>
          )}
        </div>
        <select className="admin-sort" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort by">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="most-clips">Most clips</option>
        </select>
        <button
          className="admin-sync-btn"
          onClick={syncPayouts}
          disabled={syncing}
          title="Check pending payouts against Tremendous and mark delivered/paid"
        >
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
        {syncMsg && <span className="admin-sync-msg">{syncMsg}</span>}
      </div>

      {/* ── Status tabs ─────────────────────────────────────────────────── */}
      <div className="admin-tabs" role="tablist">
        {STATUS_TABS.map((t) => {
          const count = countForTab(t.key);
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              className={'admin-tab' + (active ? ' active' : '')}
              onClick={() => setParams({ status: t.key })}
              role="tab"
              aria-selected={active}
              title={t.hint}
            >
              <span>{t.label}</span>
              {count !== undefined && <span className="admin-tab-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* ── Bulk action bar (Inbox only, shown only when items selected) ── */}
      {isSubmittedTab && selected.size > 0 && (
        <div className="admin-bulk-bar" role="region" aria-label="Bulk actions">
          <span className="bulk-count">
            <strong>{selected.size}</strong> selected
          </span>
          <div className="bulk-actions">
            <button className="link-button" onClick={clearSelection}>Clear</button>
            <button className="bulk-approve-btn" disabled={bulkBusy} onClick={bulkApprove}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 6 }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              {bulkBusy ? `Approving…` : `Approve & pay ${selected.size}`}
            </button>
          </div>
        </div>
      )}

      {/* ── Result count + select-all toggle ────────────────────────────── */}
      {total > 0 && !loading && (
        <div className="admin-meta-row">
          <span className="admin-meta-count">
            {query
              ? <>Found <strong>{total}</strong> match{total === 1 ? '' : 'es'}</>
              : <>Showing <strong>{Math.min(shown, total)}</strong> of <strong>{total}</strong></>}
          </span>
          {isSubmittedTab && visible.length > 0 && (
            <button className="admin-select-all" onClick={allVisibleSelected ? clearSelection : selectAll}>
              {allVisibleSelected ? 'Clear selection' : 'Select all visible'}
            </button>
          )}
        </div>
      )}

      {/* ── List body ───────────────────────────────────────────────────── */}
      {loading ? (
        <div className="admin-empty">
          <div className="admin-empty-text">
            <strong>Loading…</strong>
          </div>
        </div>
      ) : error ? (
        <div className="admin-empty">
          <div className="admin-empty-ico" style={{ borderColor: '#FECDD3', color: '#B42318' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <div className="admin-empty-text">
            <strong>Couldn&apos;t load reviews.</strong>
            <span>{error}</span>
          </div>
        </div>
      ) : total === 0 ? (
        <EmptyState
          tab={tab}
          view={view}
          isSubmittedTab={isSubmittedTab}
          query={query}
          onSwitchToAll={() => setView('all')}
          onInvite={() => setShowInvite(true)}
        />
      ) : (
        <>
          <div className="admin-list">
            {visible.map((s) => {
              const when    = (s.submitted_at || s.created_at || 0) * 1000;
              const ageDays = when ? Math.floor((Date.now() - when) / 86_400_000) : 0;
              const isUrgent = isSubmittedTab && ageDays >= 2;
              const isPicked = selected.has(s.id);
              const showUnread = isSubmittedTab && isUnread(s);
              return (
                <div
                  key={s.id}
                  className={'admin-row'
                    + (isPicked ? ' picked' : '')
                    + (isUrgent ? ' urgent' : '')
                    + (showUnread ? ' is-unread' : '')}
                >
                  {isSubmittedTab && (
                    <label className="admin-row-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isPicked}
                        onChange={() => toggleSelect(s.id)}
                        aria-label={`Select ${s.customer_name || s.customer_email}`}
                      />
                    </label>
                  )}
                  <button className="admin-row-body" onClick={() => openWithRead(s.id)}>
                    {showUnread && <span className="admin-row-unread-dot" aria-label="Unread"></span>}
                    <div className="admin-row-avatar" aria-hidden="true" style={avatarStyle(s.customer_name || s.customer_email)}>
                      {avatarInitials(s.customer_name || s.customer_email)}
                    </div>
                    <div className="admin-row-main">
                      <div className="admin-row-top">
                        <span className="admin-row-name">{s.customer_name || s.customer_email || 'Unknown'}</span>
                        <span className={'admin-row-when' + (isUrgent ? ' urgent' : '')}>
                          {isUrgent && <span className="urgent-dot" aria-hidden="true"></span>}
                          {relativeTime(when)}
                        </span>
                      </div>
                      <div className="admin-row-bot">
                        <span className="admin-row-email">{s.customer_email}</span>
                      </div>
                      <div className="admin-row-meta">
                        <span className="admin-row-clips">
                          <strong>{s.approved_count}</strong>/{s.clip_count} {s.clip_count === 1 ? 'clip' : 'clips'}
                        </span>
                        {s.min_verification_score != null && (
                          <span
                            className={'admin-row-aiscore score-' + verifyTier(s.min_verification_score)}
                            title={`AI scored worst clip ${s.min_verification_score}/10` + (s.avg_verification_score != null ? ` (avg ${s.avg_verification_score})` : '')}
                          >
                            AI {s.min_verification_score}/10
                          </span>
                        )}
                        {s.unverified_clip_count > 0 && (
                          <span className="admin-row-aiscore score-pending" title="Some clips haven't been AI-scored yet">
                            {s.unverified_clip_count} pending
                          </span>
                        )}
                        {s.used_count > 0 && (
                          <span className="admin-row-used">
                            <span className="row-used-dot">★</span>
                            {s.used_count} used in ads
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="admin-row-arrow" aria-hidden="true">→</span>
                  </button>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <button className="admin-load-more" onClick={() => setShown(shown + 25)}>
              Load more · {total - shown} remaining
            </button>
          )}
        </>
      )}

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onCreated={() => { setShowInvite(false); load(tab); }}
        />
      )}
    </main>
  );
}

// ── Empty-state copy varies by tab/view/query ─────────────────────────────
function EmptyState({ tab, view, isSubmittedTab, query, onSwitchToAll, onInvite }) {
  const tabHint = STATUS_TABS.find((t) => t.key === tab)?.hint || '';
  let title, body, icon;

  if (query) {
    title = 'No matches.';
    body = 'Try a different name, email, or invite token.';
    icon = (
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    );
  } else if (isSubmittedTab && view === 'unread') {
    title = 'Inbox zero. Nice.';
    body = (
      <>
        Everything in <em>Inbox</em> has been seen.{' '}
        <button className="inline-link" onClick={onSwitchToAll}>Switch to All</button> to browse the archive.
      </>
    );
    icon = (
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    );
  } else {
    title = 'Nothing here yet.';
    body = tabHint;
    icon = (
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    );
  }

  return (
    <div className="admin-empty">
      <div className="admin-empty-ico">{icon}</div>
      <div className="admin-empty-text">
        <strong>{title}</strong>
        <span>{body}</span>
        {isSubmittedTab && !query && view === 'all' && (
          <button className="inline-link" onClick={onInvite} style={{ marginTop: 6, textAlign: 'left' }}>
            Invite a customer →
          </button>
        )}
      </div>
    </div>
  );
}

// ── Invite modal — preserved from v3; chrome-only changes ─────────────────
function InviteModal({ onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [name, setName]   = useState('');
  const [hubspotId, setHubspotId] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  async function submit(e) {
    e?.preventDefault();
    setPending(true);
    setError('');
    try {
      const r = await api('/api/invite', {
        method: 'POST',
        body: {
          customer_email: email.trim(),
          customer_name: name.trim() || null,
          hubspot_contact_id: hubspotId.trim() || null,
          send_email: sendEmail,
        },
      });
      setResult(r);
    } catch (e) {
      setError(e.message || 'Failed to create invite');
    } finally {
      setPending(false);
    }
  }

  // The modal uses the kbd-help overlay primitives for the backdrop +
  // centered card — keeps the visual language consistent across the admin.
  return (
    <div className="kbd-help" onClick={onClose}>
      <div className="kbd-help-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="kbd-help-head">
          <h3>Invite a customer</h3>
          <button onClick={onClose} className="kbd-help-close" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M6 18L18 6"/>
            </svg>
          </button>
        </div>

        {result ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{
              padding: '12px 14px',
              borderRadius: 10,
              background: '#ECFDF5',
              border: '1px solid #6EE7B7',
              color: '#047857',
              fontSize: 13,
              fontWeight: 500,
            }}>
              Invite created{result.email_sent ? ' and email sent' : ''}.
            </div>
            <div>
              <label className="review-reject-label">Personal link</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="admin-search-input"
                  style={{ flex: 1, border: '1px solid var(--color-border)', borderRadius: 10, padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}
                  readOnly
                  value={result.invite_url || ''}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  className="admin-invite-btn"
                  onClick={() => { try { navigator.clipboard.writeText(result.invite_url || ''); } catch {} }}
                  style={{ flexShrink: 0, padding: '8px 14px' }}
                >
                  Copy
                </button>
              </div>
            </div>
            <button className="review-payout-btn" onClick={() => onCreated?.()} style={{ width: '100%' }}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label htmlFor="inv-email" className="review-reject-label">Customer email</label>
              <input
                id="inv-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="review-reject-input"
                style={{ minHeight: 'auto', height: 42 }}
                placeholder="customer@example.com"
              />
            </div>
            <div>
              <label htmlFor="inv-name" className="review-reject-label">Customer name (optional)</label>
              <input
                id="inv-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="review-reject-input"
                style={{ minHeight: 'auto', height: 42 }}
                placeholder="Jane Smith"
              />
            </div>
            <div>
              <label htmlFor="inv-hs" className="review-reject-label">HubSpot contact ID (optional)</label>
              <input
                id="inv-hs"
                type="text"
                value={hubspotId}
                onChange={(e) => setHubspotId(e.target.value)}
                className="review-reject-input"
                style={{ minHeight: 'auto', height: 42, fontFamily: 'monospace', fontSize: 13 }}
                placeholder="12345"
              />
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--color-fg)' }}>
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} style={{ accentColor: 'var(--voiply-orange)' }} />
              Send invitation email now
            </label>

            {error && (
              <div style={{
                padding: '10px 12px',
                borderRadius: 10,
                background: '#FEF3F2',
                border: '1px solid #FECDD3',
                color: '#B42318',
                fontSize: 13,
              }}>
                {error}
              </div>
            )}

            <div className="review-reject-actions" style={{ justifyContent: 'space-between' }}>
              <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
              <button type="submit" disabled={pending} className="btn btn-primary">
                {pending ? 'Creating…' : 'Create invite'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Same relativeTime helper as v3
function relativeTime(ms) {
  if (!ms) return '–';
  const diff = Date.now() - ms;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ms).toLocaleDateString();
}
